import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AdminTicket,
  AdminAttachment,
  AdminTicketPriorite,
  AdminTicketStatut,
} from "@victor/api-client/console";
import { api } from "../../lib/api";
import { PageSpinner } from "../../ui/Spinner";
import { Button } from "../../ui/Button";
import { Select } from "../../ui/Select";
import { Textarea } from "../../ui/Textarea";
import { Input } from "../../ui/Input";
import { Modal } from "../../ui/Modal";
import { Panel, Badge, EmptyInline, fmtDate, fmtDateTime, fmtMoney, initials } from "../tenants/tabs/_ui";

const STATUTS: AdminTicketStatut[] = ["ouvert", "en_cours", "en_attente", "resolu", "ferme"];
const PRIORITES: AdminTicketPriorite[] = ["basse", "normale", "haute", "urgente"];

const STATUT_LABEL: Record<AdminTicketStatut, string> = {
  ouvert: "Ouvert",
  en_cours: "En cours",
  en_attente: "En attente",
  resolu: "Résolu",
  ferme: "Fermé",
};
const STATUT_TONE: Record<AdminTicketStatut, "accent" | "amber" | "navy" | "emerald" | "slate"> = {
  ouvert: "accent",
  en_cours: "amber",
  en_attente: "navy",
  resolu: "emerald",
  ferme: "slate",
};
const PRIO_LABEL: Record<AdminTicketPriorite, string> = {
  basse: "Basse",
  normale: "Normale",
  haute: "Haute",
  urgente: "Urgente",
};
export function SupportInboxPage() {
  const [statut, setStatut] = useState("");
  const [priorite, setPriorite] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["admin-support", statut, priorite],
    queryFn: () =>
      api.support.list({
        statut: statut || undefined,
        priorite: priorite || undefined,
      }),
    refetchInterval: 20_000,
  });

  const tickets = q.data ?? [];
  const ouverts = tickets.filter((t) => t.statut !== "ferme" && t.statut !== "resolu").length;

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-navy-800 sm:text-3xl">
            Support
          </h1>
          <p className="mt-1 text-navy-600">
            Conversations clients en direct.{" "}
            {ouverts > 0 ? (
              <span className="font-semibold text-accent-600">
                {ouverts} en cours
              </span>
            ) : (
              "Tout est traité ✨"
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select
            compact
            options={[{ value: "", label: "Tous les statuts" }, ...STATUTS.map((s) => ({ value: s, label: STATUT_LABEL[s] }))]}
            value={statut}
            onChange={(e) => setStatut(e.target.value)}
          />
          <Select
            compact
            options={[{ value: "", label: "Toutes priorités" }, ...PRIORITES.map((p) => ({ value: p, label: PRIO_LABEL[p] }))]}
            value={priorite}
            onChange={(e) => setPriorite(e.target.value)}
          />
        </div>
      </header>

      {q.isLoading ? (
        <PageSpinner />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
          {/* Liste */}
          <div className="space-y-2">
            {tickets.length === 0 ? (
              <Panel>
                <EmptyInline>Aucun ticket.</EmptyInline>
              </Panel>
            ) : (
              tickets.map((t) => (
                <TicketRow
                  key={t.id}
                  t={t}
                  active={selected === t.id}
                  onClick={() => setSelected(t.id)}
                />
              ))
            )}
          </div>

          {/* Détail */}
          <div>
            {selected ? (
              <TicketDetail id={selected} />
            ) : (
              <div className="flex h-full min-h-[300px] items-center justify-center rounded-2xl bg-white ring-1 ring-navy-100">
                <div className="text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-navy-50 text-navy-300">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
                  </div>
                  <p className="text-sm text-navy-500">
                    Sélectionnez une conversation pour la traiter.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TicketRow({
  t,
  active,
  onClick,
}: {
  t: AdminTicket;
  active: boolean;
  onClick: () => void;
}) {
  const needsReply = t.lastAuthorType === "tenant";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block w-full rounded-2xl bg-white p-3.5 text-left ring-1 transition-all ${
        active
          ? "ring-2 ring-accent-400 shadow-sm"
          : "ring-navy-100 hover:-translate-y-0.5 hover:shadow-sm hover:ring-navy-300"
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-navy-100 text-xs font-bold text-navy-600">
          {initials(t.agence)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[11px] text-navy-400">{t.numero}</span>
            <Badge tone={STATUT_TONE[t.statut]} dot>
              {STATUT_LABEL[t.statut]}
            </Badge>
          </div>
          <p className="mt-0.5 truncate text-sm font-semibold text-navy-800">
            {t.sujet}
          </p>
          <p className="truncate text-xs text-navy-500">{t.agence}</p>
          <div className="mt-1.5 flex items-center justify-between gap-2">
            {needsReply ? (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-accent-600">
                <span className="h-1.5 w-1.5 rounded-full bg-accent-500" />
                à répondre
              </span>
            ) : (
              <span className="text-[11px] text-navy-300">répondu</span>
            )}
            <span className="text-[11px] text-navy-400">{fmtDate(t.lastActiviteAt)}</span>
          </div>
        </div>
      </div>
    </button>
  );
}

async function downloadAdmin(key: string, filename: string) {
  try {
    const blob = await api.support.downloadAttachment(key);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  } catch {
    // ignore
  }
}

function TicketDetail({ id }: { id: string }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const [macrosOpen, setMacrosOpen] = useState(false);
  const [pending, setPending] = useState<AdminAttachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastTyping = useRef(0);

  const onUpload = async (file: File) => {
    setUploading(true);
    try {
      const a = await api.support.uploadAttachment(id, file);
      setPending((p) => [...p, a]);
    } catch {
      // ignore
    } finally {
      setUploading(false);
    }
  };

  const macros = useQuery({
    queryKey: ["support-macros"],
    queryFn: () => api.support.macros(),
  });
  const insertMacro = (body: string) =>
    setReply((r) => (r.trim() ? `${r}\n\n${body}` : body));

  const onReplyChange = (v: string) => {
    setReply(v);
    const now = Date.now();
    if (now - lastTyping.current > 3000) {
      lastTyping.current = now;
      api.support.typing(id).catch(() => {});
    }
  };

  const q = useQuery({
    queryKey: ["admin-ticket", id],
    queryFn: () => api.support.get(id),
    refetchInterval: 5_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["admin-ticket", id] });
    qc.invalidateQueries({ queryKey: ["admin-support"] });
  };

  const send = useMutation({
    mutationFn: () => api.support.reply(id, reply, pending),
    onSuccess: () => {
      setReply("");
      setPending([]);
      invalidate();
    },
  });
  const setStatutM = useMutation({
    mutationFn: (s: AdminTicketStatut) => api.support.setStatut(id, s),
    onSuccess: invalidate,
  });
  const setPrioriteM = useMutation({
    mutationFn: (p: AdminTicketPriorite) => api.support.setPriorite(id, p),
    onSuccess: invalidate,
  });

  if (q.isLoading || !q.data) return <PageSpinner />;
  const t = q.data;

  return (
    <div className="space-y-4">
      <ContextPanel id={id} tenantId={t.tenantId} />

      <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-navy-100 shadow-sm">
        {/* En-tête conversation */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-navy-100 bg-gradient-to-r from-navy-50/80 to-white px-5 py-3.5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-navy-100 text-xs font-bold text-navy-600">
              {initials(t.agence)}
            </span>
            <div>
              <p className="text-sm font-bold text-navy-800">
                {t.sujet}{" "}
                <span className="font-mono text-[11px] font-normal text-navy-400">
                  {t.numero}
                </span>
              </p>
              <p className="text-xs text-navy-500">{t.agence}</p>
              {t.csatScore !== null ? (
                <p
                  className={`mt-0.5 text-xs font-semibold ${t.csatScore === 1 ? "text-emerald-600" : "text-amber-700"}`}
                  title={t.csatComment ?? undefined}
                >
                  {t.csatScore === 1 ? "😊 Satisfait" : "😞 Insatisfait"}
                  {t.csatComment ? ` — « ${t.csatComment} »` : ""}
                </p>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select
              compact
              options={STATUTS.map((s) => ({ value: s, label: STATUT_LABEL[s] }))}
              value={t.statut}
              onChange={(e) => setStatutM.mutate(e.target.value as AdminTicketStatut)}
            />
            <Select
              compact
              options={PRIORITES.map((p) => ({ value: p, label: PRIO_LABEL[p] }))}
              value={t.priorite}
              onChange={(e) => setPrioriteM.mutate(e.target.value as AdminTicketPriorite)}
            />
          </div>
        </div>

        {/* Fil */}
        <div className="max-h-[46vh] space-y-3 overflow-y-auto bg-navy-50/40 p-5">
          {t.messages.map((m) => {
            const hubblot = m.authorType === "hubblot";
            return (
              <div key={m.id} className={`flex items-end gap-2 ${hubblot ? "justify-end" : "justify-start"}`}>
                {!hubblot ? (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-navy-200 text-[10px] font-bold text-navy-600">
                    {initials(t.agence)}
                  </span>
                ) : null}
                <div
                  className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                    hubblot
                      ? "rounded-br-sm bg-accent-500 text-white"
                      : "rounded-bl-sm bg-white text-navy-800 ring-1 ring-navy-100"
                  }`}
                >
                  <p className={`mb-0.5 text-[11px] font-semibold ${hubblot ? "text-white/75" : "text-navy-400"}`}>
                    {m.authorType === "hubblot" ? "Vous" : m.authorType === "tenant" ? "Agence" : "Assistant"}
                    {" · "}
                    {fmtDateTime(m.createdAt)}
                  </p>
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  {m.attachments?.length ? (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {m.attachments.map((a) => (
                        <button
                          key={a.key}
                          type="button"
                          onClick={() => downloadAdmin(a.key, a.filename)}
                          className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium ring-1 transition-colors ${
                            hubblot
                              ? "bg-white/15 text-white ring-white/20 hover:bg-white/25"
                              : "bg-white text-navy-700 ring-navy-100 hover:bg-navy-50"
                          }`}
                        >
                          📎 {a.filename}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {/* Composer */}
        <div className="border-t border-navy-100 p-4">
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {(macros.data ?? []).map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => insertMacro(m.body)}
                title={m.body}
                className="rounded-full bg-navy-50 px-2.5 py-1 text-xs font-medium text-navy-700 ring-1 ring-navy-100 transition-colors hover:bg-navy-100"
              >
                {m.titre}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setMacrosOpen(true)}
              className="text-xs font-semibold text-accent-600 hover:underline"
            >
              {(macros.data ?? []).length > 0
                ? "Gérer les macros"
                : "+ Réponses pré-enregistrées"}
            </button>
          </div>
          <Textarea
            label="Réponse au client"
            rows={3}
            value={reply}
            onChange={(e) => onReplyChange(e.target.value)}
            placeholder="Écrivez votre réponse… (le client voit que vous écrivez)"
          />
          {pending.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {pending.map((a) => (
                <span
                  key={a.key}
                  className="inline-flex items-center gap-1.5 rounded-full bg-navy-50 px-2.5 py-1 text-xs text-navy-700 ring-1 ring-navy-100"
                >
                  📎 {a.filename}
                  <button
                    type="button"
                    onClick={() => setPending((p) => p.filter((x) => x.key !== a.key))}
                    className="text-navy-400 hover:text-red-500"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          ) : null}
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              title="Joindre un fichier"
              className="flex h-9 w-9 items-center justify-center rounded-full text-navy-500 ring-1 ring-navy-200 transition-colors hover:bg-navy-50 disabled:opacity-50"
            >
              {uploading ? "…" : "📎"}
            </button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onUpload(f);
                e.target.value = "";
              }}
            />
            <Button
              onClick={() => reply.trim() && send.mutate()}
              disabled={send.isPending || !reply.trim()}
            >
              {send.isPending ? "Envoi…" : "Répondre"}
            </Button>
          </div>
        </div>
      </div>

      <MacrosManager open={macrosOpen} onClose={() => setMacrosOpen(false)} />
    </div>
  );
}

function MacrosManager({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["support-macros"],
    queryFn: () => api.support.macros(),
  });
  const [titre, setTitre] = useState("");
  const [body, setBody] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["support-macros"] });
  const reset = () => {
    setTitre("");
    setBody("");
    setEditId(null);
  };

  const save = useMutation({
    mutationFn: () =>
      editId
        ? api.support.updateMacro(editId, { titre, body })
        : api.support.createMacro({ titre, body }),
    onSuccess: () => {
      reset();
      invalidate();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.support.deleteMacro(id),
    onSuccess: invalidate,
  });

  return (
    <Modal open={open} onClose={onClose} title="Réponses pré-enregistrées" size="lg">
      <div className="space-y-4">
        <ul className="divide-y divide-navy-100 rounded-xl ring-1 ring-navy-100">
          {(q.data ?? []).length === 0 ? (
            <li className="px-4 py-3 text-sm text-navy-400">Aucune macro.</li>
          ) : (
            (q.data ?? []).map((m) => (
              <li key={m.id} className="flex items-start justify-between gap-3 px-4 py-3">
                <div className="min-w-0">
                  <p className="font-semibold text-navy-800">{m.titre}</p>
                  <p className="truncate text-xs text-navy-500">{m.body}</p>
                </div>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => {
                      setEditId(m.id);
                      setTitre(m.titre);
                      setBody(m.body);
                    }}
                    className="rounded-lg px-2 py-1 text-xs font-semibold text-navy-600 hover:bg-navy-50"
                  >
                    Éditer
                  </button>
                  <button
                    type="button"
                    onClick={() => del.mutate(m.id)}
                    className="rounded-lg px-2 py-1 text-xs font-semibold text-red-600 hover:bg-red-50"
                  >
                    Suppr.
                  </button>
                </div>
              </li>
            ))
          )}
        </ul>

        <form
          className="space-y-3 rounded-xl bg-navy-50/50 p-4 ring-1 ring-navy-100"
          onSubmit={(e) => {
            e.preventDefault();
            if (titre.trim() && body.trim()) save.mutate();
          }}
        >
          <p className="text-xs font-bold uppercase tracking-wider text-navy-400">
            {editId ? "Modifier la macro" : "Nouvelle macro"}
          </p>
          <Input
            label="Titre"
            value={titre}
            onChange={(e) => setTitre(e.target.value)}
          />
          <Textarea
            label="Texte de la réponse"
            rows={4}
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            {editId ? (
              <Button type="button" variant="ghost" onClick={reset}>
                Annuler
              </Button>
            ) : null}
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? "Enregistrement…" : editId ? "Mettre à jour" : "Ajouter"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

function daysSinceLabel(iso: string): string {
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days < 31) return `il y a ${days} j`;
  const months = Math.floor(days / 30);
  if (months < 12) return `il y a ${months} mois`;
  const years = Math.floor(days / 365);
  const rem = Math.floor((days - years * 365) / 30);
  return `il y a ${years} an${years > 1 ? "s" : ""}${rem ? ` ${rem} mois` : ""}`;
}

function Metric({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-xl p-3 ring-1 ${
        accent ? "bg-accent-50 ring-accent-100" : "bg-navy-50/60 ring-navy-100"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wider text-navy-400">
        {label}
      </p>
      <p className={`mt-0.5 text-sm font-bold ${accent ? "text-accent-700" : "text-navy-800"}`}>
        {value}
      </p>
      {sub ? <p className="text-xs text-navy-500">{sub}</p> : null}
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="text-sm text-navy-500">{label}</span>
      <span className="text-right text-sm font-semibold text-navy-800">
        {value ?? "—"}
      </span>
    </div>
  );
}

function ContextPanel({ id, tenantId }: { id: string; tenantId: string }) {
  const [fiche, setFiche] = useState(false);
  const q = useQuery({
    queryKey: ["admin-ticket-context", id],
    queryFn: () => api.support.context(id),
  });
  if (!q.data) return null;
  const c = q.data;

  return (
    <div className="overflow-hidden rounded-2xl bg-white ring-1 ring-navy-100 shadow-sm">
      {/* Bandeau identité */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-navy-800 to-accent-700 px-5 py-3.5 text-white">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15 text-sm font-bold">
            {initials(c.tenant?.raisonSociale ?? "?")}
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-bold leading-tight">
                {c.tenant?.raisonSociale ?? "—"}
              </p>
              {c.tenant ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-semibold">
                  {c.tenant.statut}
                </span>
              ) : null}
            </div>
            {c.contact ? (
              <p className="truncate text-[12px] text-white/75">
                {c.contact.nom}
                {c.contact.roles ? ` · ${c.contact.roles}` : ""} · {c.contact.email}
              </p>
            ) : null}
          </div>
        </div>
        <button
          type="button"
          onClick={() => setFiche(true)}
          className="shrink-0 rounded-full bg-white/15 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/25"
        >
          Voir la fiche complète
        </button>
      </div>

      {/* Métriques */}
      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4">
        <Metric
          label="Client depuis"
          value={c.tenant ? fmtDate(c.tenant.clientDepuis) : "—"}
          sub={c.tenant ? daysSinceLabel(c.tenant.clientDepuis) : undefined}
        />
        <Metric
          label="Abonnement"
          value={c.abonnement?.planCode ?? c.tenant?.plan ?? "—"}
          sub={
            c.abonnement
              ? `${c.abonnement.statut} · ${fmtMoney(c.abonnement.mrrHt)} €/mois`
              : undefined
          }
        />
        <Metric
          accent
          label="Total payé"
          value={`${fmtMoney(c.finances.totalPayeTtc)} €`}
          sub={`${c.finances.nbFacturesPayees} facture${c.finances.nbFacturesPayees > 1 ? "s" : ""}`}
        />
        <Metric
          label="Volume"
          value={`${c.compteurs.agents} agents`}
          sub={`${c.compteurs.etablissements} étab. · ${c.compteurs.utilisateurs} users`}
        />
      </div>

      {c.contact?.derniereConnexion ? (
        <p className="px-4 pb-3 text-xs text-navy-400">
          Dernière connexion : {fmtDateTime(c.contact.derniereConnexion)}
        </p>
      ) : null}

      <Modal
        open={fiche}
        onClose={() => setFiche(false)}
        size="lg"
        title={`Fiche client — ${c.tenant?.raisonSociale ?? ""}`}
      >
        <div className="space-y-5">
          <FicheSection title="Contact">
            <Row label="Nom" value={c.contact?.nom} />
            <Row label="Email" value={c.contact?.email} />
            <Row label="Rôles" value={c.contact?.roles || "—"} />
            <Row label="Fonction" value={c.contact?.fonction} />
            <Row
              label="Dernière connexion"
              value={c.contact?.derniereConnexion ? fmtDateTime(c.contact.derniereConnexion) : "—"}
            />
          </FicheSection>
          <FicheSection title="Société">
            <Row label="Raison sociale" value={c.tenant?.raisonSociale} />
            <Row label="Statut" value={c.tenant?.statut} />
            <Row label="Ville" value={c.tenant?.ville} />
            <Row label="SIRET" value={c.tenant?.siret} />
            <Row
              label="Client depuis"
              value={c.tenant ? `${fmtDate(c.tenant.clientDepuis)} (${daysSinceLabel(c.tenant.clientDepuis)})` : "—"}
            />
          </FicheSection>
          <FicheSection title="Abonnement & finances">
            <Row label="Plan" value={c.abonnement?.planCode ?? c.tenant?.plan} />
            <Row label="Statut abonnement" value={c.abonnement?.statut} />
            <Row label="Cycle" value={c.abonnement?.cycle} />
            <Row label="Base HT" value={c.abonnement ? `${fmtMoney(c.abonnement.basePrixHt)} €` : "—"} />
            <Row label="MRR estimé" value={c.abonnement ? `${fmtMoney(c.abonnement.mrrHt)} €/mois HT` : "—"} />
            <Row
              label="Total payé"
              value={`${fmtMoney(c.finances.totalPayeTtc)} € TTC (${c.finances.nbFacturesPayees} factures)`}
            />
          </FicheSection>
          <FicheSection title="Volume">
            <Row label="Agents" value={c.compteurs.agents} />
            <Row label="Établissements" value={c.compteurs.etablissements} />
            <Row label="Utilisateurs" value={c.compteurs.utilisateurs} />
          </FicheSection>

          <div className="flex justify-end pt-1">
            <a
              href={`/tenants/${tenantId}`}
              target="_blank"
              rel="noopener"
              className="inline-flex items-center gap-1.5 rounded-full bg-navy-800 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-700"
            >
              Ouvrir la fiche détaillée ↗
            </a>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function FicheSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <p className="mb-2 text-xs font-bold uppercase tracking-wider text-navy-400">
        {title}
      </p>
      <div className="divide-y divide-navy-100 rounded-xl bg-navy-50/50 px-4 ring-1 ring-navy-100">
        {children}
      </div>
    </section>
  );
}
